import { Injectable, inject } from '@angular/core';
import { catchError, from, map, Observable, of, shareReplay, take, tap } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams, } from '@angular/common/http';

import {
  Drgs,
  DrgFreqDist,
  ProviderSummary,
  DrgIncident,
  ClinicClaims,
  ProviderClaim,
  ClaimRate,
  ClaimRateByProvider,
  ClaimsByClinic,
  MedicalExpensesProvider,
  MedicalExpensesProviderPercentage,
  AvgClaimDRG,
  HospDurationDRG,
  Incidentclaims,
  IncidentType,
  Accommodation,
  KpiData,
  IncidentSeverity,
  PolicyArea,
  KpiCardI,
  GroupedFraudData,
  PDropdownOptions,
  PaginatedProvidersResponse,
  GroupedAgents,
  FraudTotalsReportDto
} from '../../interfaces';
// import { API_URL } from '../../utils';
import { API_URL } from '../../utils/injection-tokens/api-url.token';
import { FilterOption } from '../../partial-components';
import { TopInvestigatorAdjustersDto } from '../../interfaces/reports/top-investigator-adjuster-dto';
import { ClaimsSummaryByAdjusterInvestigatorAndApproverDto } from '../../interfaces/reports/claims-summary-by-adjuster-investigator-and-approver';
import { AgentProductionDto } from '../../interfaces/reports/agent-production-dto';
import { CoverageByAgentRowDto } from '../../interfaces/reports/coverage-by-agent-dto';
import { RepairShopReportDto } from '../../interfaces/reports/repair-shop-report-dto';
import { ClaimsPerBrokerDto } from '../../interfaces/reports/adjuster-graph';
import { FwaCasesByInvestigatorReportDto } from '../../interfaces/reports/fwa-cases-by-investigator-report-dto';
import { FwaCasesByInvestigatorTagDto } from '../../interfaces/reports/fwa-cases-by-investigator-tag-dto';
import { FwaSavingsReportDto } from '../../interfaces/reports/fwa-savings-report-dto';
import { AccidentCareBenchmarkingReportDto } from '../../interfaces/reports/accident-care-benchmarking-report-dto';
import { ClaimsPerAdjusterReportDto } from '../../interfaces/reports/claims-per-adjuster-report-dto';
import { BacklogReportDto } from '../../interfaces/reports/backlog-report-dto';

@Injectable({
  providedIn: 'root',
})
export class ReportDataService {
  private http = inject(HttpClient);
  private apiUrl = inject(API_URL);
  private chartFiltersCache$?: Observable<FilterOption[]>;

  readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control':
        'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
      Pragma: 'no-cache',
      Expires: '0',
    }),
  };

  getDRGSummaryProvider(year: string, contractType: string,quarter:string, groupByKenHospitalCode: boolean = false): Observable<ProviderSummary[]> {
    const params = new HttpParams()
      .set('year', year)
      .set('contractType', contractType)
      .set('quarter', quarter.toString())
      .set("groupByKenHospitalCode", groupByKenHospitalCode.toString());

    return this.http.get<ProviderSummary[]>(
      `${this.apiUrl}reports/drgProviderSummary`,
      {
        ...this.httpOptions,
        params
      }
    );
  }
  getProviderClaims(): Observable<ProviderClaim[]> {
    return this.http.get<ProviderClaim[]>(
      `${this.apiUrl}reports/providerClaims/`,
      this.httpOptions
    ).pipe(
      map((items) => items = items.slice(0, 5))
    );
  }
  getClaimDatesRate(year: string, contractType: string, quarter:string): Observable<ClaimRate[]> {
    const params = new HttpParams()
      .set('year', year)
      .set('contractType', contractType)
      .set('quarter', quarter);
    return this.http.get<ClaimRate[]>(
      `${this.apiUrl}reports/claimDateRate/`,
      {
        ...this.httpOptions,
        params
      }
    );
  }
  getClaimRate(
    year: string,
    claimType: string = 'All',
    quarter: string = 'All'
  ): Observable<ClaimRate[]> {
    let params = new HttpParams()
      .set('year', year)
      .set('quarter', quarter);
    
    if (claimType !== 'All') {
      params = params.set('claimType', claimType);
    }
    
    return this.http.get<ClaimRate[]>(
      `${this.apiUrl}reports/claimRate/`,
      {
        ...this.httpOptions,
        params
      }
    );
  }

  getClaimRateByProvider(): Observable<ClaimRateByProvider> {
    return this.http.get<ClaimRateByProvider>(
      `${this.apiUrl}reports/claimRateByProvider/`,
      this.httpOptions
    );
  }

  getClaimsSummaryByClinic(): Observable<ClaimsByClinic> {
    return this.http.get<ClaimsByClinic>(
      `${this.apiUrl}reports/claimsSummaryByClinic/`,
      this.httpOptions
    );
  }
  getMedicalExpensesDistribution(year: string, contractType: string, quarter:string): Observable<{ category: string; grossAmount: number }[]> {
    const params = new HttpParams()
      .set('year', year)
      .set('contractType', contractType)
      .set('quarter',quarter);
    return this.http.get<{ category: string; grossAmount: number }[]>(
      `${this.apiUrl}reports/providerMedExpDist/`,
      {
        ...this.httpOptions,
        params
      }

    );
  }

  getMedicalExpensesDistributionPercPage(pageNumber = 1, pageSize = 10): Observable<PaginatedProvidersResponse> {
    const params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize);

    return this.http.get<PaginatedProvidersResponse>(
      `${this.apiUrl}reports/providerMedExpPercDist/`,
      { ...this.httpOptions, params }
    ).pipe(catchError((err) => of({} as PaginatedProvidersResponse)));;
  }

  getAvgClaimPerDRG(): Observable<AvgClaimDRG> {
    return this.http.get<AvgClaimDRG>(
      `${this.apiUrl}reports/avgClaimPerDRG/`,
      this.httpOptions
    );
  }

  getHospDurationPerDRG(): Observable<HospDurationDRG> {
    return this.http.get<HospDurationDRG>(
      `${this.apiUrl}reports/hospDurationPerDRG/`,
      this.httpOptions
    );
  }

  getIncidentClaims(year: string, contractType: string, quarter:string): Observable<Incidentclaims[]> {
    const params = new HttpParams()
      .set('year', year)
      .set('contractType', contractType)
      .set('quarter', quarter);
    return this.http.get<Incidentclaims[]>(
      `${this.apiUrl}reports/incidentClaims/`,
      {
        ...this.httpOptions,
        params
      }
    );
  }

  getIncidentTypes(year: string, claimType: string = 'All'): Observable<IncidentType[]> {
    let params = new HttpParams()
      .set('year', year);
    
    if (claimType !== 'All') {
      params = params.set('claimType', claimType);
    }
    
    return this.http.get<IncidentType[]>(
      `${this.apiUrl}reports/incidentType/`,
      {
        ...this.httpOptions,
        params
      }
    );
  }

  getAccommodationClaims(year: string, contractType: string, quarter:string): Observable<Accommodation[]> {
    const params = new HttpParams()
      .set('year', year)
      .set('contractType', contractType)
      .set('quarter', quarter);
    return this.http.get<Accommodation[]>(
      `${this.apiUrl}reports/accommodationClaims/`,
      {
        ...this.httpOptions,
        params
      }
    );
  }

  getContractReport(case_code: string) {
    let code: number;
    code = Number(case_code);
    return this.http.get(`${this.apiUrl}reports/${code}/contracts_report/en`);
  }
  getCostAnalysisReport(case_code: string) {
    let code: number;
    code = Number(case_code);
    return this.http.get(`${this.apiUrl}reports/${code}/cost_report/en`);
  }
  getConsumptionAnalysis(case_code: string) {
    let code: number;
    code = Number(case_code);
    return this.http.get(`${this.apiUrl}reports/${code}/consumption_report/en`);
  }

  getCostAnalysisDummyData(case_id: string): Observable<string> {
    return this.http.get(
      `assets/data/case_detail_reports/case_id_${case_id}/costanalysis.html`,
      { responseType: 'text' }
    );
  }
  getConsumptionAnalysisDummyData(case_id: string): Observable<string> {
    return this.http.get(
      `assets/data/case_detail_reports/case_id_${case_id}/consumptionanalysis.html`,
      { responseType: 'text' }
    );
  }
  getContractCheckDummyData(case_id: string): Observable<string> {
    return this.http.get(
      `assets/data/case_detail_reports/case_id_${case_id}/contractcheck.html`,
      { responseType: 'text' }
    );
  }
  getKpiData(year: string, contractType: string, quarter:string): Observable<KpiData[]> {
    const params = new HttpParams()
      .set('year', year)
      .set('contractType', contractType)
      .set('quarter',quarter);
    return this.http.get<KpiData[]>(
      `${this.apiUrl}reports/kpiData/`,
      {
        ...this.httpOptions,
        params
      }
    );
  }

  getIncidentSeverity(year: string, claimType: string = 'All'): Observable<IncidentSeverity[]> {
    let params = new HttpParams()
      .set('year', year);
    
    if (claimType !== 'All') {
      params = params.set('claimType', claimType);
    }
    
    return this.http.get<IncidentSeverity[]>(
      `${this.apiUrl}reports/incidentSeverity/`,
      { 
        ...this.httpOptions,
        params
      }
    );
  }

  getPolicyArea(
    year: string,
    claimType: string = 'All',
    quarter: string = 'All'
  ): Observable<PolicyArea[]> {
    let params = new HttpParams()
      .set('year', year)
      .set('quarter', quarter);
    
    if (claimType !== 'All') {
      params = params.set('claimType', claimType);
    }
    
    return this.http.get<PolicyArea[]>(
      `${this.apiUrl}reports/policyArea/`,
      {
        ...this.httpOptions,
        params
      }
    );
  }

  getChartFilters() {
    if (!this.chartFiltersCache$) {
      this.chartFiltersCache$ = this.http
        .get<FilterOption[]>(`${this.apiUrl}Charts/dashboard-filters`)
    }

    return this.chartFiltersCache$;
  }

  getCoreactKpiData(
    year: string,
    claimType: string = 'All',
    quarter: string = 'All'
  ): Observable<KpiData[]> {
    let params = new HttpParams()
      .set('year', year)
      .set('quarter', quarter);
    
    if (claimType !== 'All') {
      params = params.set('claimType', claimType);
    }
    
    return this.http.get<KpiData[]>(
      `${this.apiUrl}reports/kpisData/`,
      {
        ...this.httpOptions,
        params
      }
    );
  }

  getIconByLabel(label: string): string {
    switch (label) {
      case 'Total Claims':
        return 'fas fa-file-alt';
      case 'Pending Claims':
        return 'fas fa-euro-sign';
      case 'Total Claim Amount':
        return 'fas fa-euro-sign';
      case 'Approved Claim Amount':
        return 'fas fa-exclamation-circle';
      case 'Pending Claim Amount':
        return 'fas fa-exclamation-circle';
      case 'Average Claim Duration':
        return 'fas fa-clock fa-xl';
      default:
        return 'fas fa-question-circle';
    }
  }

  getUnitForKpi(item: KpiData): string | undefined {
    if (item.name === 'Average Claim Duration') {
      return 'Days';
    }
    return '';
  }

  getGroupedAgents(
    year: string,
    claimType: string = 'All',
    quarter: string = 'All'
  ): Observable<GroupedAgents[]> {
    let params = new HttpParams()
      .set('year', year)
      .set('quarter', quarter);
    
    if (claimType !== 'All') {
      params = params.set('claimType', claimType);
    }
    
    return this.http.get<GroupedAgents[]>(
      `${this.apiUrl}reports/groupedAgents/`,
      {
        ...this.httpOptions,
        params
      }
    );
  }
    getClaimsPerCover(
      year: string,
      claimType: string = 'All',
      quarter: string = 'All'
    ): Observable<IncidentType[]> {
    let params = new HttpParams()
      .set('year', year)
      .set('quarter', quarter);
    
    if (claimType !== 'All') {
      params = params.set('claimType', claimType);
    }
    
    return this.http.get<IncidentType[]>(
      `${this.apiUrl}reports/claimsPerCover/`,
      {
        ...this.httpOptions,
        params
      }
    );
  }

    getClaimsPerGeographicalArea(
      year: string,
      claimType: string = 'All',
      quarter: string = 'All'
    ): Observable<IncidentSeverity[]> {
    let params = new HttpParams()
      .set('year', year)
      .set('quarter', quarter);
    
    if (claimType !== 'All') {
      params = params.set('claimType', claimType);
    }
    
    return this.http.get<IncidentSeverity[]>(
      `${this.apiUrl}reports/claimsPerGeographicalArea/`,
      { 
        ...this.httpOptions,
        params
      }
    );
  }

  getCoversPerGeographicalArea(
    year: string,
    claimType: string = 'All',
    quarter: string = 'All',
    coverage: string = 'All',
    area: string = 'All'
  ): Observable<IncidentSeverity[]> {
    let params = new HttpParams()
      .set('year', year)
      .set('quarter', quarter);

    if (claimType !== 'All') {
      params = params.set('claimType', claimType);
    }
    if (coverage && coverage !== 'All') {
      params = params.set('coverage', coverage);
    }
    if (area && area !== 'All') {
      params = params.set('area', area);
    }

    return this.http.get<IncidentSeverity[]>(
      `${this.apiUrl}reports/coversPerGeographicalArea/`,
      {
        ...this.httpOptions,
        params
      }
    );
  }

  getAccidentCountries(): Observable<string[]> {
    return this.http.get<string[]>(
      `${this.apiUrl}reports/areas-ordered-by-count`,
      { ...this.httpOptions }
    );
  }
   getTopInvestigatorAdjusters(
    year: string,
    contractType: string,
    jobRole: string,
    quarter: string = 'All'
  ): Observable<TopInvestigatorAdjustersDto[]> {
    const params = new HttpParams()
      .set('year', year)
      .set('contractType', contractType)
      .set('quarter', quarter)
      .set("jobRole", jobRole);

    return this.http.get<TopInvestigatorAdjustersDto[]>(
      `${this.apiUrl}reports/top-10-investigators-adjusters`,
      {
        ...this.httpOptions,
        params
      }
    );
  }

  getClaimsSummaryByAdjusterInvestigatorAndApprover(
    year: string = 'All',
    quarter?: string | null
  ): Observable<ClaimsSummaryByAdjusterInvestigatorAndApproverDto[]> {

    let params = new HttpParams().set('year', year);

    if (quarter) {
      params = params.set('quarter', quarter);
    }

    return this.http.get<ClaimsSummaryByAdjusterInvestigatorAndApproverDto[]>(
      `${this.apiUrl}reports/claims-summary-by-adjuster-investigator-and-approver`,
      {
        ...this.httpOptions,
        params
      }
    );
  }

  getAllAgents(searchTerm?: string | null): Observable<PDropdownOptions[]> {
    
    let params = new HttpParams();
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    return this.http.get<PDropdownOptions[]>(
      `${this.apiUrl}agents`,
      { ...this.httpOptions, params }
    );
  }

  getAllRepairShops(searchTerm?: string | null): Observable<PDropdownOptions[]> {
    let params = new HttpParams();
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    return this.http.get<PDropdownOptions[]>(
      `${this.apiUrl}repairshops`,
      { ...this.httpOptions, params }
    );
  }

  getAllCoverages(): Observable<PDropdownOptions[]> {
    return this.http.get<PDropdownOptions[]>(
      `${this.apiUrl}coverages`,
      { ...this.httpOptions }
    );
  }

  getAgentProduction(
    year: string = 'All',
    quarter?: string | null,
    fromDate?: string | null,
    toDate?: string | null,
    agent?: string | null
  ): Observable<AgentProductionDto[]> {

    let params = new HttpParams().set('year', year);

    if (quarter) {
      params = params.set('quarter', quarter);
    }

    if (fromDate) {
      params = params.set('fromDate', fromDate);
    }

    if (toDate) {
      params = params.set('toDate', toDate);
    }

    if (agent && agent !== 'All') {
      params = params.set('agent', agent);
    }

    return this.http.get<AgentProductionDto[]>(
      `${this.apiUrl}reports/agent-production`,
      {
        ...this.httpOptions,
        params,
      }
    );
  }

  getCoverageByAgent(
    year: string = 'All',
    quarter?: string | null,
    fromDate?: string | null,
    toDate?: string | null,
    agent?: string | null,
    coverage?: string | null
  ): Observable<CoverageByAgentRowDto[]> {
    let params = new HttpParams().set('year', year);

    if (quarter) {
      params = params.set('quarter', quarter);
    }

    if (fromDate) {
      params = params.set('fromDate', fromDate);
    }

    if (toDate) {
      params = params.set('toDate', toDate);
    }

    if (agent && agent !== 'All') {
      params = params.set('agent', agent);
    }

    if (coverage && coverage !== 'All') {
      params = params.set('coverage', coverage);
    }

    return this.http.get<CoverageByAgentRowDto[]>(
      `${this.apiUrl}reports/coverage-by-agent`,
      {
        ...this.httpOptions,
        params,
      }
    );
  }

  getRepairShopReport(
    year: string = 'All',
    quarter?: string | null,
    fromDate?: string | null,
    toDate?: string | null,
    repairShop?: string | null,
    afm?: string | null
  ): Observable<RepairShopReportDto[]> {
    let params = new HttpParams().set('year', year);

    if (quarter) {
      params = params.set('quarter', quarter);
    }
    if (fromDate) {
      params = params.set('fromDate', fromDate);
    }
    if (toDate) {
      params = params.set('toDate', toDate);
    }
    if (repairShop && repairShop !== 'All') {
      params = params.set('repairShop', repairShop);
    }
    if (afm) {
      params = params.set('afm', afm);
    }

    return this.http.get<RepairShopReportDto[]>(
      `${this.apiUrl}reports/repair-shop-report`,
      { params }
    );
  }

  getFraudTotalsReport(
    year: string = 'All',
    quarter?: string | null
  ): Observable<FraudTotalsReportDto> {

    let params = new HttpParams().set('year', year);

    if (quarter) {
      params = params.set('quarter', quarter);
    }

    return this.http.get<FraudTotalsReportDto>(
      `${this.apiUrl}reports/fraud-totals-report`,
      { params }
    );
  }

  getWatchlistReport(options: {
    valueType?: ValueType | null;
    valueIn?: string | null;
    entryTypeCv?: string | null;
    year?: string;
    quarter?: string | null;
  }): Observable<any[]> {
    let params = new HttpParams();

    if (options.valueType != null) {
      params = params.set('ValueType', options.valueType.toString());
    }
    if (options.valueIn) {
      params = params.set('ValueIn', options.valueIn);
    }
    if (options.entryTypeCv) {
      params = params.set('EntryTypeCv', options.entryTypeCv);
    }
    if (options.year && options.year !== 'All') {
      params = params.set('Year', options.year);
    } else {
      params = params.set('Year', 'All');
    }
    if (options.quarter != null) {
      params = params.set('Quarter', options.quarter);
    }

    return this.http
      .get<WatchListReportResponse[]>(`${this.apiUrl}reports/watchlist-report`, {
        headers: this.httpOptions.headers,
        params,
      })
      .pipe(
        catchError((error) => {
          console.error('Error fetching watchlist report', error);
          return of([] as WatchListReportResponse[]);
        })
      );
  }

  getFwaCasesByInvestigator(
    year: string = 'All',
    quarter?: string | null,
    name?: string | null
  ): Observable<FwaCasesByInvestigatorReportDto> {
    let params = new HttpParams();

    if (year && year !== 'All') {
      params = params.set('year', year);
    }

    if (quarter) {
      params = params.set('quarter', quarter);
    }

    if (name && name !== 'All') {
      params = params.set('name', name);
    }

    return this.http.get<FwaCasesByInvestigatorReportDto>(
      `${this.apiUrl}reports/fwa-cases-by-investigator`,
      {
        ...this.httpOptions,
        params,
      }
    );
  }

  getFwaCasesByInvestigatorTag(
    year: string = 'All',
    quarter?: string | null,
    tagName?: string | null,
    fromDate?: string | null,
    toDate?: string | null
  ): Observable<FwaCasesByInvestigatorTagDto[]> {
    let params = new HttpParams();

    if (year && year !== 'All') {
      params = params.set('year', year);
    }

    if (quarter) {
      params = params.set('quarter', quarter);
    }

    if (tagName && tagName !== 'All') {
      params = params.set('tagName', tagName);
    }

    if (fromDate) {
      params = params.set('fromDate', fromDate);
    }

    if (toDate) {
      params = params.set('toDate', toDate);
    }

    return this.http.get<FwaCasesByInvestigatorTagDto[]>(
      `${this.apiUrl}reports/fwa-cases-by-investigator-tag`,
      {
        ...this.httpOptions,
        params,
      }
    );
  }

  getFwaSavingsReport(
    year: string = 'All',
    quarter?: string | null,
    fromDate?: string | null,
    toDate?: string | null
  ): Observable<FwaSavingsReportDto> {
    let params = new HttpParams();

    if (year && year !== 'All') {
      params = params.set('year', year);
    }

    if (quarter) {
      params = params.set('quarter', quarter);
    }

    if (fromDate) {
      params = params.set('fromDate', fromDate);
    }

    if (toDate) {
      params = params.set('toDate', toDate);
    }

    return this.http.get<FwaSavingsReportDto>(
      `${this.apiUrl}reports/fwa-savings-report`,
      {
        ...this.httpOptions,
        params,
      }
    );
  }

  getClaimsPerBroker(
    year: string = 'All',
    claimType: string = 'All',
    agent: string = 'All',
    quarter: string = 'All'
  ): Observable<ClaimsPerBrokerDto[]> {
    let params = new HttpParams()
      .set('year', year || 'All')
      .set('claimType', claimType || 'All')
      .set('agent', agent || 'All')
      .set('quarter', quarter || 'All');

    return this.http.get<ClaimsPerBrokerDto[]>(
      `${this.apiUrl}reports/claims-per-broker`,
      { params }
    );
  }

  getReportsDates(): Observable<{ years: number[]; quarters: number[] }> {
    if (!this._reportsDates$) {
      this._reportsDates$ = this.http.get<{ years: number[]; quarters: number[] }>(
        `${this.apiUrl}reports/reports-dates`,
        this.httpOptions
      );
    }
    return this._reportsDates$;
  }

  private _reportsDates$: Observable<{ years: number[]; quarters: number[] }> | null = null;

  getAccidentCareBenchmarkingReport(
    year: string = 'All',
    quarter?: string | null,
    fromDate?: string | null,
    toDate?: string | null
  ): Observable<AccidentCareBenchmarkingReportDto> {
    let params = new HttpParams();

    if (year && year !== 'All') {
      params = params.set('year', year);
    }

    if (quarter) {
      params = params.set('quarter', quarter);
    }

    if (fromDate) {
      params = params.set('fromDate', fromDate);
    }

    if (toDate) {
      params = params.set('toDate', toDate);
    }

    return this.http.get<AccidentCareBenchmarkingReportDto>(
      `${this.apiUrl}reports/accident-care-benchmarking`,
      {
        ...this.httpOptions,
        params,
      }
    );
  }

  getClaimsPerAdjusterReport(
    year: string | null,
    quarter?: string | null,
    fromDate?: string | null,
    toDate?: string | null,
    adjuster?: string | null
  ): Observable<ClaimsPerAdjusterReportDto[]> {
    let params = new HttpParams();

    if(year != null) {
      params = new HttpParams().set('year', year.toString());
    }      
    if (quarter) {
      params = params.set('quarter', quarter);
    }
    if (fromDate) {
      params = params.set('fromDate', fromDate);
    }
    if (toDate) {
      params = params.set('toDate', toDate);
    }
    if (adjuster && adjuster !== 'All') {
      params = params.set('adjuster', adjuster);
    }

    return this.http.get<ClaimsPerAdjusterReportDto[]>(
      `${this.apiUrl}reports/claims-per-adjuster`,
      {
        ...this.httpOptions,
        params,
      }
    );
  }

  getBacklogReport(
    year: string | null,
    quarter?: string | null,
    fromDate?: string | null,
    toDate?: string | null,
    adjuster?: string | null
  ): Observable<BacklogReportDto[]> {
    let params = new HttpParams();

    if(year != null) {
      params = new HttpParams().set('year', year.toString());
    }      
    if (quarter) {
      params = params.set('quarter', quarter);
    }
    if (fromDate) {
      params = params.set('fromDate', fromDate);
    }
    if (toDate) {
      params = params.set('toDate', toDate);
    }
    if (adjuster && adjuster !== 'All') {
      params = params.set('adjuster', adjuster);
    }

    return this.http.get<BacklogReportDto[]>(
      `${this.apiUrl}reports/backlog-report`,
      {
        ...this.httpOptions,
        params,
      }
    );
  }

  getAllAdjusters(): Observable<string[]> {
    return this.http.get<string[]>(
      `${this.apiUrl}adjusters`,
      this.httpOptions
    );
  }
}

export enum ValueType {
  PlateNo = 2,
  AFM = 3,
  ClaimNo = 4,
  Name = 5,
}

export interface WatchListReportResponse {
  ClaimNumber?: string;
  FullName?: string;
  Identifier?: string;
  CreatedAt?: string;
  Source?: string;
  EntryTypeCv?: string;
  VehicleRegistrationNo?: string;
}
